import { Opportunity, Requirement, DraftSection } from "../types";

export interface MasterProposalCompilerParams {
  opportunity: Opportunity;
  requirements: Requirement[];
  draftSections: DraftSection[];
}

/**
 * Sorts requirements in logical solicitation sequence:
 * 1. By sectionRef (e.g. "1.1", "1.2", "2.0") if present
 * 2. By category hierarchy: general -> technical -> qualifications -> management -> pricing -> compliance
 */
export function sortRequirementsInProposalOrder(requirements: Requirement[]): Requirement[] {
  const categoryOrder: Record<string, number> = {
    general: 1,
    technical: 2,
    qualifications: 3,
    management: 4,
    pricing: 5,
    compliance: 6,
  };

  return [...requirements].sort((a, b) => {
    // If both have sectionRef, compare naturally
    if (a.sectionRef && b.sectionRef) {
      return a.sectionRef.localeCompare(b.sectionRef, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    }
    if (a.sectionRef && !b.sectionRef) return -1;
    if (!a.sectionRef && b.sectionRef) return 1;

    const catA = categoryOrder[a.category] || 99;
    const catB = categoryOrder[b.category] || 99;
    if (catA !== catB) return catA - catB;

    return a.title.localeCompare(b.title);
  });
}

/**
 * Compiles the complete Master Proposal into formatted Markdown
 */
export function compileMasterProposalMarkdown(params: MasterProposalCompilerParams): string {
  const { opportunity, requirements, draftSections } = params;
  const sortedReqs = sortRequirementsInProposalOrder(requirements);
  const sectionMap = new Map<string, DraftSection>();
  draftSections.forEach((s) => sectionMap.set(s.requirementId, s));

  const totalWords = draftSections.reduce((sum, s) => sum + (s.wordCount || 0), 0);
  const lines: string[] = [];

  // Cover Page
  lines.push(`# PROPOSAL RESPONSE`);
  lines.push(`## ${opportunity.title}`);
  lines.push(`**Prepared for:** ${opportunity.client}`);
  lines.push(`**Prepared by:** Multilingual Connections`);
  lines.push(
    `**Submission Date:** ${opportunity.dueDate}${opportunity.dueTime ? " at " + opportunity.dueTime : ""}`,
  );
  lines.push(`**Primary Lead:** ${opportunity.owner}`);
  if (opportunity.leadSignOff) {
    lines.push(
      `**Lead Sign-Off:** Signed by ${opportunity.leadSignOff.signerName} (${opportunity.leadSignOff.signedAt.slice(0, 10)})`,
    );
  }
  if (opportunity.executiveSignOff) {
    lines.push(
      `**Executive Approval:** Signed by ${opportunity.executiveSignOff.signerName} (${opportunity.executiveSignOff.signedAt.slice(0, 10)})`,
    );
  }
  lines.push(``);
  lines.push(`---`);
  lines.push(``);

  // Table of Contents
  lines.push(`## Table of Contents`);
  sortedReqs.forEach((r, idx) => {
    const refPrefix = r.sectionRef ? `${r.sectionRef} ` : `${idx + 1}. `;
    lines.push(`- [${refPrefix}${r.title}](#${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")})`);
  });
  lines.push(``);
  lines.push(`---`);
  lines.push(``);

  // Proposal Sections
  sortedReqs.forEach((r, idx) => {
    const draft = sectionMap.get(r.id);
    const refPrefix = r.sectionRef ? `${r.sectionRef} ` : `${idx + 1}. `;
    lines.push(`### ${refPrefix}${r.title}`);
    lines.push(
      `*Category: ${r.category.toUpperCase()} | Mandatory: ${r.mandatory ? "Yes" : "No"}*`,
    );
    lines.push(``);

    if (draft && draft.content.trim()) {
      lines.push(draft.content.trim());
    } else {
      lines.push(`*(No draft narrative provided for this requirement.)*`);
    }
    lines.push(``);
    lines.push(`---`);
    lines.push(``);
  });

  // Appendix: Compliance Matrix
  lines.push(`## Appendix: Requirements & Compliance Matrix`);
  lines.push(`| Ref | Requirement Title | Category | Mandatory | Status | Words |`);
  lines.push(`| :--- | :--- | :--- | :--- | :--- | :--- |`);
  sortedReqs.forEach((r) => {
    const draft = sectionMap.get(r.id);
    const words = draft?.wordCount || 0;
    const status = draft?.status?.toUpperCase() || "UNWRITTEN";
    lines.push(
      `| ${r.sectionRef || "—"} | ${r.title} | ${r.category} | ${r.mandatory ? "YES" : "NO"} | ${status} | ${words} |`,
    );
  });
  lines.push(``);
  lines.push(`*Total Proposal Word Count: ${totalWords.toLocaleString()} words*`);

  return lines.join("\n");
}

/**
 * Compiles the complete Master Proposal into clean, printable HTML with embedded styles for browser print / PDF export
 */
export function compileMasterProposalHtml(params: MasterProposalCompilerParams): string {
  const { opportunity, requirements, draftSections } = params;
  const sortedReqs = sortRequirementsInProposalOrder(requirements);
  const sectionMap = new Map<string, DraftSection>();
  draftSections.forEach((s) => sectionMap.set(s.requirementId, s));

  const totalWords = draftSections.reduce((sum, s) => sum + (s.wordCount || 0), 0);
  const estimatedPages = Math.max(1, Math.ceil(totalWords / 450));

  const sectionsHtml = sortedReqs
    .map((r, idx) => {
      const draft = sectionMap.get(r.id);
      const refPrefix = r.sectionRef ? `${escapeHtml(r.sectionRef)} ` : `${idx + 1}. `;
      const rawContent = draft?.content?.trim() || "";
      const formattedContent = rawContent
        ? rawContent
            .split(/\n\n+/)
            .map((p) => `<p>${escapeHtml(p).replace(/\n/g, "<br/>")}</p>`)
            .join("\n")
        : `<p class="placeholder-text"><em>(No narrative provided for this section.)</em></p>`;

      return `
        <div class="proposal-section">
          <div class="section-header">
            <span class="section-ref">${refPrefix}</span>
            <h2 class="section-title">${escapeHtml(r.title)}</h2>
          </div>
          <div class="section-meta">
            <span class="badge ${r.mandatory ? "badge-mandatory" : "badge-optional"}">${r.mandatory ? "MANDATORY" : "OPTIONAL"}</span>
            <span class="badge badge-category">${escapeHtml(r.category.toUpperCase())}</span>
            ${draft ? `<span class="badge badge-status">${escapeHtml(draft.status.toUpperCase())}</span>` : ""}
            <span class="meta-words">${(draft?.wordCount || 0).toLocaleString()} words</span>
          </div>
          <div class="section-body">
            ${formattedContent}
          </div>
        </div>
      `;
    })
    .join("\n");

  const matrixRowsHtml = sortedReqs
    .map((r) => {
      const draft = sectionMap.get(r.id);
      return `
        <tr>
          <td>${escapeHtml(r.sectionRef || "—")}</td>
          <td>${escapeHtml(r.title)}</td>
          <td>${escapeHtml(r.category)}</td>
          <td>${r.mandatory ? '<span class="status-yes">YES</span>' : "NO"}</td>
          <td>${draft ? escapeHtml(draft.status.toUpperCase()) : "UNWRITTEN"}</td>
          <td>${(draft?.wordCount || 0).toLocaleString()}</td>
        </tr>
      `;
    })
    .join("\n");

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(opportunity.title)} — Final Proposal Package</title>
  <style>
    @page {
      size: letter;
      margin: 1in;
      @bottom-right {
        content: counter(page);
      }
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      color: #1e293b;
      line-height: 1.6;
      font-size: 11pt;
      margin: 0;
      padding: 0;
    }
    .cover-page {
      min-height: 80vh;
      display: flex;
      flex-direction: column;
      justify-content: center;
      border-bottom: 2px solid #2ba5b5;
      padding-bottom: 3rem;
      margin-bottom: 3rem;
      page-break-after: always;
    }
    .org-banner {
      font-size: 12pt;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.1em;
      color: #2ba5b5;
      margin-bottom: 1rem;
    }
    .cover-title {
      font-size: 26pt;
      font-weight: 800;
      color: #0f172a;
      line-height: 1.2;
      margin: 0 0 1.5rem 0;
    }
    .cover-meta {
      font-size: 12pt;
      color: #475569;
      margin-top: 2rem;
      line-height: 1.8;
    }
    .cover-meta strong {
      color: #0f172a;
    }
    .signoff-box {
      margin-top: 2.5rem;
      padding: 1rem 1.5rem;
      background: #f8fafc;
      border-left: 4px solid #2ba5b5;
      border-radius: 4px;
      font-size: 10pt;
    }
    .toc-container {
      page-break-after: always;
      margin-bottom: 3rem;
    }
    .toc-title {
      font-size: 18pt;
      font-weight: 700;
      color: #0f172a;
      border-bottom: 1px solid #cbd5e1;
      padding-bottom: 0.5rem;
      margin-bottom: 1.5rem;
    }
    .toc-list {
      list-style: none;
      padding: 0;
      margin: 0;
    }
    .toc-item {
      display: flex;
      justify-content: space-between;
      padding: 0.35rem 0;
      border-bottom: 1px dotted #e2e8f0;
      font-size: 10.5pt;
    }
    .proposal-section {
      margin-bottom: 2.5rem;
      page-break-inside: avoid;
    }
    .section-header {
      display: flex;
      align-items: baseline;
      gap: 0.5rem;
      border-bottom: 1px solid #e2e8f0;
      padding-bottom: 0.4rem;
      margin-bottom: 0.5rem;
    }
    .section-ref {
      font-size: 14pt;
      font-weight: 700;
      color: #2ba5b5;
    }
    .section-title {
      font-size: 14pt;
      font-weight: 700;
      color: #0f172a;
      margin: 0;
    }
    .section-meta {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      font-size: 8.5pt;
    }
    .badge {
      display: inline-block;
      padding: 0.15rem 0.45rem;
      border-radius: 3px;
      font-weight: 600;
      text-transform: uppercase;
    }
    .badge-mandatory { background: #fee2e2; color: #991b1b; }
    .badge-optional { background: #f1f5f9; color: #475569; }
    .badge-category { background: #e0f2fe; color: #0369a1; }
    .badge-status { background: #dcfce7; color: #166534; }
    .meta-words { color: #64748b; margin-left: auto; }
    .section-body {
      font-size: 10.5pt;
      color: #334155;
    }
    .section-body p {
      margin: 0 0 1rem 0;
    }
    .placeholder-text {
      color: #94a3b8;
    }
    .matrix-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9pt;
      margin-top: 1rem;
    }
    .matrix-table th, .matrix-table td {
      border: 1px solid #cbd5e1;
      padding: 0.4rem 0.6rem;
      text-align: left;
    }
    .matrix-table th {
      background: #f1f5f9;
      font-weight: 700;
    }
    .status-yes {
      font-weight: 700;
      color: #047857;
    }
    @media print {
      body { margin: 0; padding: 0; }
      .no-print { display: none !important; }
      .proposal-section { page-break-inside: auto; }
    }
  </style>
</head>
<body>
  <!-- Cover Page -->
  <div class="cover-page">
    <div class="org-banner">Multilingual Connections · Official Proposal Response</div>
    <h1 class="cover-title">${escapeHtml(opportunity.title)}</h1>
    <div class="cover-meta">
      <div><strong>Client Agency:</strong> ${escapeHtml(opportunity.client)}</div>
      <div><strong>Submission Deadline:</strong> ${escapeHtml(opportunity.dueDate)}${opportunity.dueTime ? " at " + escapeHtml(opportunity.dueTime) : ""}</div>
      <div><strong>Proposal Lead:</strong> ${escapeHtml(opportunity.owner)}</div>
      <div><strong>Total Estimated Length:</strong> ~${estimatedPages} pages (${totalWords.toLocaleString()} words)</div>
    </div>
    ${
      opportunity.leadSignOff || opportunity.executiveSignOff
        ? `
        <div class="signoff-box">
          <strong>Official Proposal Sign-Off:</strong><br/>
          ${
            opportunity.leadSignOff
              ? `Lead Sign-Off: ${escapeHtml(opportunity.leadSignOff.signerName)} (${escapeHtml(opportunity.leadSignOff.signerEmail)}) on ${escapeHtml(opportunity.leadSignOff.signedAt.slice(0, 10))}<br/>`
              : ""
          }
          ${
            opportunity.executiveSignOff
              ? `Executive Authorization: ${escapeHtml(opportunity.executiveSignOff.signerName)} (${escapeHtml(opportunity.executiveSignOff.signerEmail)}) on ${escapeHtml(opportunity.executiveSignOff.signedAt.slice(0, 10))}`
              : ""
          }
        </div>
      `
        : ""
    }
  </div>

  <!-- Table of Contents -->
  <div class="toc-container">
    <h2 class="toc-title">Table of Contents</h2>
    <ul class="toc-list">
      ${sortedReqs
        .map((r, idx) => {
          const refPrefix = r.sectionRef ? `${escapeHtml(r.sectionRef)} ` : `${idx + 1}. `;
          return `
            <li class="toc-item">
              <span>${refPrefix}${escapeHtml(r.title)}</span>
              <span>${escapeHtml(r.category.toUpperCase())}</span>
            </li>
          `;
        })
        .join("\n")}
    </ul>
  </div>

  <!-- Proposal Sections -->
  ${sectionsHtml}

  <!-- Appendix -->
  <div style="page-break-before: always; margin-top: 3rem;">
    <h2 class="toc-title">Appendix: Requirements & Compliance Matrix</h2>
    <table class="matrix-table">
      <thead>
        <tr>
          <th>Ref</th>
          <th>Requirement Title</th>
          <th>Category</th>
          <th>Mandatory</th>
          <th>Status</th>
          <th>Word Count</th>
        </tr>
      </thead>
      <tbody>
        ${matrixRowsHtml}
      </tbody>
    </table>
  </div>
</body>
</html>`;
}

function escapeHtml(text: string): string {
  if (!text) return "";
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/**
 * Downloads a string as a client file
 */
export function downloadCompiledDocument(
  filename: string,
  content: string,
  mimeType = "text/plain;charset=utf-8",
): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
